import { Breadcrumb, Button, Modal, Table, TextInput } from "flowbite-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import {
  HiHome,
  HiOutlineExclamationCircle,
  HiOutlinePencilAlt,
  HiPlus,
  HiTrash,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";

import { ToastContainer, toast } from "react-toastify";
import {
  listTraditionalWorkouts,
  deleteTraditionalWorkout,
} from "../../services/traditionalWorkouts";

import moment from "moment-timezone";

import {
  BreadcrumbItem,
  Label,
  ModalBody,
  ModalHeader,
  Pagination,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import { type WorkoutListItemResponse } from "types/traditionalWorkouts";

const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const MAX_CONTENT_PER_PAGE = 10;

const TraditionalWorkoutsListPage: FC = function () {
  const navigate = useNavigate();
  const [workouts, setWorkouts] = useState<WorkoutListItemResponse[]>([]);
  const [loading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);

  const onPageChange = (page: number) => {
    setCurrentPage(page);
    getWorkoutData(page);
  };

  const getWorkoutData = async (page = currentPage) => {
    setIsLoading(true);
    try {
      const response = await listTraditionalWorkouts();

      if (response.results && Array.isArray(response.results)) {
        let filteredWorkouts = response.results;

        if (search) {
          filteredWorkouts = response.results.filter((w) =>
            w.title.toLowerCase().includes(search.toLowerCase()),
          );
        }

        const start = (page - 1) * MAX_CONTENT_PER_PAGE;
        const end = start + MAX_CONTENT_PER_PAGE;
        const paginatedWorkouts = filteredWorkouts.slice(start, end);

        setWorkouts(paginatedWorkouts);
        setTotal(filteredWorkouts.length);
      } else {
        setWorkouts([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching workouts:", error);
      toast.error("Failed to load traditional workouts");
      setWorkouts([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getWorkoutData();
  }, []);

  const getAllWorkouts = () => {
    getWorkoutData(currentPage);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
    getWorkoutData(1);
  };

  return (
    <NavbarSidebarLayout isFooter={false}>
      <div className="block items-center justify-between border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:flex">
        <div className="mb-1 w-full">
          <div className="mb-4">
            <Breadcrumb className="mb-4">
              <BreadcrumbItem href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">Home</span>
                </div>
              </BreadcrumbItem>
              <BreadcrumbItem>Traditional Workouts</BreadcrumbItem>
              <BreadcrumbItem>List</BreadcrumbItem>
            </Breadcrumb>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
              All Traditional Workouts
            </h1>
          </div>
          <div className="sm:flex">
            <div className="mb-3 hidden items-center dark:divide-gray-700 sm:mb-0 sm:flex sm:divide-x sm:divide-gray-100">
              <form className="lg:pr-3">
                <Label htmlFor="users-search" className="sr-only">
                  Search
                </Label>
                <div className="relative mt-1 lg:w-64 xl:w-96">
                  <TextInput
                    id="users-search"
                    name="users-search"
                    placeholder="Search for traditional workouts"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      handleSearch(e.target.value);
                    }}
                  />
                </div>
              </form>
            </div>
            <div className="ml-auto flex items-center space-x-2 sm:space-x-3">
              <Button
                color="primary"
                onClick={() => navigate("/traditional-workouts/new")}
              >
                <div className="flex items-center gap-x-2">
                  <HiPlus className="text-xl" />
                  Add Traditional Workout
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow">
              {loading ? (
                <>
                  <div role="status" className="text-center">
                    <svg
                      aria-hidden="true"
                      className=" mx-auto my-10 size-8  animate-spin fill-blue-600  text-center text-gray-200 dark:text-gray-600"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                    <span className="sr-only">Loading...</span>
                  </div>
                </>
              ) : (
                <>
                  <AllTraditionalWorkoutsTable
                    workouts={workouts}
                    state={loading}
                    getWorkouts={getAllWorkouts}
                  />
                  {total > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      onPageChange={onPageChange}
                      showIcons
                      aria-disabled={true}
                      totalPages={Math.ceil(
                        Number(total || 1) / MAX_CONTENT_PER_PAGE,
                      )}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        rtl={false}
        pauseOnFocusLoss
        pauseOnHover
        theme="dark"
      />
    </NavbarSidebarLayout>
  );
};

interface AllTraditionalWorkoutsTableProps {
  workouts: WorkoutListItemResponse[];
  state: boolean;
  getWorkouts: () => void;
}

const AllTraditionalWorkoutsTable: FC<AllTraditionalWorkoutsTableProps> =
  function ({ workouts, getWorkouts }) {
    const navigate = useNavigate();

    return (
      <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
        <TableHead className="bg-gray-100 dark:bg-gray-700">
          <TableHeadCell>Workout</TableHeadCell>
          <TableHeadCell>Trainer</TableHeadCell>
          <TableHeadCell>Status</TableHeadCell>
          <TableHeadCell>Actions</TableHeadCell>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
          {workouts?.length ? (
            <>
              {workouts.map((workout) => {
                return (
                  <TableRow
                    key={workout.id}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <TableCell className="whitespace-nowrap px-6 py-4">
                      <div className="flex items-center space-x-4">
                        {workout.thumbnail_image_url ? (
                          <img
                            className="size-10 rounded-full shrink-0 object-cover"
                            src={workout.thumbnail_image_url}
                            alt="thumbnail"
                          />
                        ) : (
                          <div className="size-10 rounded-full shrink-0 bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              N/A
                            </span>
                          </div>
                        )}
                        <div className="text-sm font-normal text-gray-500 dark:text-gray-400">
                          <div className="text-base font-semibold text-gray-900 dark:text-white">
                            {workout.title}
                          </div>
                          <div className="text-sm font-normal text-gray-500 dark:text-gray-400">
                            {moment(workout.created_at)
                              .tz("America/Los_Angeles")
                              .format("dddd, MMMM Do YYYY, h:mm:ss a")}
                          </div>
                          {workout.tags && workout.tags.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {workout.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="whitespace-nowrap px-6 py-4 text-base font-normal text-gray-900 dark:text-white">
                      {workout.trainer ? workout.trainer.title : "-"}
                    </TableCell>

                    <TableCell className="whitespace-nowrap px-6 py-4 text-base font-normal text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <div
                          className={`mr-2 size-2.5 rounded-full ${
                            workout.processing_status === "COMPLETE"
                              ? "bg-green-400"
                              : workout.processing_status === "PROCESSING"
                                ? "bg-yellow-400"
                                : "bg-red-400"
                          }`}
                        ></div>{" "}
                        {toTitleCase(workout.processing_status)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-x-3 whitespace-nowrap">
                        <Button
                          color="primary"
                          onClick={() =>
                            navigate(`/traditional-workouts/${workout.id}/edit`)
                          }
                        >
                          <div className="flex items-center gap-x-2">
                            <HiOutlinePencilAlt className="text-lg" />
                            Edit
                          </div>
                        </Button>
                        <DeleteTraditionalWorkoutModal
                          workout={workout}
                          getWorkouts={getWorkouts}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </>
          ) : (
            <>
              <TableRow className="hover:bg-gray-100 dark:hover:bg-gray-700">
                <TableCell colSpan={4}>No traditional workouts found</TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    );
  };

interface DeleteTraditionalWorkoutModalProps {
  workout: WorkoutListItemResponse;
  getWorkouts: () => void;
}

const DeleteTraditionalWorkoutModal: FC<DeleteTraditionalWorkoutModalProps> =
  function ({ workout, getWorkouts }) {
    const [isOpen, setOpen] = useState(false);
    const [deleting, setDelete] = useState(false);

    return (
      <>
        <Button color="failure" onClick={() => setOpen(true)}>
          <div className="flex items-center gap-x-2">
            <HiTrash className="text-lg" />
            Delete
          </div>
        </Button>
        <Modal onClose={() => setOpen(false)} show={isOpen} size="md">
          <ModalHeader className="px-6 pb-0 pt-6">
            <span className="sr-only">Delete Traditional Workout</span>
          </ModalHeader>
          <ModalBody className="px-6 pb-6 pt-0">
            <div className="flex flex-col items-center gap-y-6 text-center">
              <HiOutlineExclamationCircle className="text-7xl text-red-500" />
              <p className="text-xl text-gray-500">
                Are you sure you want to delete this traditional workout?
              </p>
              <p className="text-base font-semibold text-gray-700 dark:text-gray-300">
                "{workout.title}"
              </p>
              <div className="flex items-center gap-x-3">
                <Button
                  color="failure"
                  disabled={deleting}
                  onClick={async () => {
                    setDelete(true);

                    try {
                      await deleteTraditionalWorkout(workout.id);
                      toast.success(
                        `Traditional workout "${workout.title}" deleted successfully`,
                      );
                      getWorkouts();
                      setOpen(false);
                    } catch (error) {
                      console.error("Error deleting workout:", error);
                      toast.error("Failed to delete traditional workout");
                    } finally {
                      setDelete(false);
                    }
                  }}
                >
                  {deleting ? "Deleting..." : "Yes, I'm sure"}
                </Button>
                <Button color="light" onClick={() => setOpen(false)}>
                  No, cancel
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>
      </>
    );
  };

export default TraditionalWorkoutsListPage;
